import { urlSlotToKey, slotKeyToUrl } from '@/features/matches/matchSlots';
import { observeRecordingSeek, recordingSeek } from '@/features/playback/recordingSeek';
import { PlayerTransport } from "@/features/playback/PlayerTransport";
import {
  ArrowLeft,
  Cast,
  ChevronLeft,
  ChevronRight,
  Inbox,
  PencilLine,
  RefreshCw,
} from "lucide-react";
import {
  slotsForMatch,
  SLOT_LABELS,
  type MatchSlot,
} from "@/features/matches/matchSlots";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  errorMessage,
  getAppSettings,
  getMatchBySlug,
  getPublicTeam,
  getPublicStreamUrls,
  regenerateAdminThumbnail,
  resolvePublicAirPlayHlsUrl,
} from "@/lib/api";
import { coerceBool } from "@/lib/coerce";
import { getSideLogos } from "@/lib/matchLogos";
import {
  publicAppSettingsKey,
  unscopedKey,
  useViewerHostKey,
} from "@/lib/queryClient";
import { useToast } from "@/components/ui/toast";
import { useMe } from "@/features/auth/useMe";
import { useDocumentMeta } from "@/lib/useDocumentMeta";
import {
  getPlaybackSpeed,
  getResumePosition,
  savePlaybackPosition,
} from "@/lib/playbackMemory";
import { hasAdminAccess } from "@/features/admin/nav";
import { HlsVideo } from "@/features/playback/HlsVideo";
import { useChromecast } from "@/features/playback/useChromecast";
import { useVodHeartbeat } from "@/features/playback/useVodHeartbeat";
import { useAuthStore } from "@/stores/auth";
import { useScoreRevealStore, useScoreRevealed } from "@/stores/scoreReveal";
import type { AppSettings, Match } from "@/lib/types";
import {
  firstReadySlot,
  hasRecordedScore,
} from "@/features/season/seasonHelpers";
import { usePublicVideoShortcuts } from "../hooks/usePublicVideoShortcuts";
import { shootoutLabel } from "@/features/matches/matchResult";
import { ClubMark } from "@/components/ClubMark";

/**
 * M11a section B — public game view at `/match/$slug` and
 * `/match/$slug/$slot`.
 *
 * Three responsibilities:
 *   1. Resolve the slug through the bounded viewer-visible match endpoint. If
 *      the slug isn't found, render a "not found" shell with a back link
 *      instead of throwing.
 *   2. Mount the HLS player for the active slot. Active slot
 *      defaults to the match's first ready slot when the URL
 *      omits one; explicit `/$slot` URLs win.
 *   3. Wire the side concerns: position memory, heartbeat,
 *      keyboard shortcuts, AirPlay/Chromecast buttons, score-reveal, OG
 *      meta tags.
 *
 * The legacy `js/player.js` was ~700 LOC; this React port is
 * smaller because M5 already shipped `<HlsVideo>` + the heartbeat
 * hook (which handles kill-propagation toast + auto-pause). The
 * public game view reuses both with the no-token URL shape.
 */
/** Legacy URL ↔ backend slot transform.
 *
 * The backend's `match.video_status` keys are underscored
 * (`first_half`, `second_half`, `full`). The legacy public URLs
 * used hyphenated forms in the path (`/match/foo/second-half`)
 * for readability. To keep old shareable links working AND emit
 * the same canonical URL shape, normalize both directions:
 *
 *   URL "second-half"  →  slot key "second_half"  (read)
 *   slot key "second_half"  →  URL "second-half"  (write)
 *
 * Reviewer-flagged on PR #21 P1.
 */
export { urlSlotToKey, slotKeyToUrl } from '@/features/matches/matchSlots';

function tenantTeamName(match: Match, sourceTeamName?: string): string {
  if (sourceTeamName) return sourceTeamName;
  if (match.team_id && match.home_team_id === match.team_id) {
    return match.home_team;
  }
  if (match.team_id && match.away_team_id === match.team_id) {
    return match.away_team;
  }
  return match.home_team;
}

function longMatchDate(value: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

/** "Back to Matches" link for the public game view.
 *
 * When the viewer arrived from a team page, `fromTeamSlug` is the
 * `?from=` breadcrumb — the link returns to `/teams/$teamSlug` so the
 * back motion is coherent. Absent (season view / direct link) ⇒ the
 * link falls back to `/`, the legacy behavior. */
function BackToMatchesLink({
  fromTeamSlug,
  fromSeason,
  label = "← Back to matches",
  className,
}: {
  fromTeamSlug: string | undefined;
  fromSeason: string | undefined;
  label?: React.ReactNode;
  className?: string;
}) {
  if (fromTeamSlug) {
    return (
      <Link
        to="/teams/$teamSlug"
        params={{ teamSlug: fromTeamSlug }}
        search={{ season: fromSeason, view: "matches" }}
        data-testid="game-back-link"
        className={className}
      >
        {label}
      </Link>
    );
  }
  return (
    <Link to="/" data-testid="game-back-link" className={className}>
      {label}
    </Link>
  );
}

export function GameShell() {
  const params = useParams({ strict: false });
  const slug = (params as { slug?: string }).slug ?? "";
  const slotParam = urlSlotToKey((params as { slot?: string }).slot);
  const navigate = useNavigate();
  // `?from=<teamSlug>` — set when the viewer arrived from a team page
  // (`/teams/$teamSlug`). Drives the "Back to Matches" link target so
  // it returns to that team's match list instead of the marketing
  // landing. `strict: false` because the param-shape is shared across
  // both `/match/$slug` and `/match/$slug/$slot`.
  const search = useSearch({ strict: false });
  const fromTeamSlug = (search as { from?: string }).from;
  const fromSeason = (search as { season?: string }).season;
  const meQuery = useMe();
  const authProbePending = meQuery.isPending && !meQuery.isFetched;
  const matchesQueryKey = useViewerHostKey(
    "public-matches",
    // The endpoint is now a single-match lookup, so the slug must always be
    // part of the cache key. Adjacent-match navigation commonly omits the
    // optional `from` hint; sharing a key in that case would keep rendering
    // the previous match instead of fetching the new slug.
    { teamSlug: fromTeamSlug ?? "", gameSlug: slug },
  );
  const sourceTeamKey = useViewerHostKey("public-team", {
    slug: fromTeamSlug ?? "",
  });

  const matchesQuery = useQuery({
    queryKey: matchesQueryKey,
    queryFn: () => getMatchBySlug(slug),
    enabled: !authProbePending,
    retry: (failureCount, error) =>
      error instanceof ApiError && (error.status === 403 || error.status === 404)
        ? false
        : failureCount < 3,
    staleTime: 60_000,
  });
  const sourceTeamQuery = useQuery({
    queryKey: sourceTeamKey,
    queryFn: () => getPublicTeam(fromTeamSlug ?? ""),
    enabled: !authProbePending && Boolean(fromTeamSlug),
    staleTime: 60_000,
  });
  const settingsQuery = useQuery({
    queryKey: publicAppSettingsKey(),
    queryFn: getAppSettings,
    staleTime: 5 * 60_000,
  });
  const match = matchesQuery.data?.match;
  const sortedMatches = useMemo(
    () =>
      matchesQuery.data
        ? [
            matchesQuery.data.previous_match,
            matchesQuery.data.match,
            matchesQuery.data.next_match,
          ].filter((candidate): candidate is Match => candidate !== null)
        : [],
    [matchesQuery.data],
  );

  // Keep a valid explicit segment selected even when its recording is absent.
  // This lets extra-time and shootout deep links show their own honest empty
  // state instead of silently substituting the first ready half.
  const activeSlot = useMemo(() => {
    if (!match) return null;
    const availableSlots = slotsForMatch(match);
    if (slotParam && availableSlots.includes(slotParam as MatchSlot)) {
      return slotParam;
    }
    return firstReadySlot(match) ?? availableSlots[0] ?? null;
  }, [match, slotParam]);

  if (authProbePending || matchesQuery.isLoading) {
    return (
      <main
        data-testid="game-loading"
        className="min-h-screen bg-canvas text-ink-muted flex items-center justify-center"
      >
        Loading match…
      </main>
    );
  }
  if (
    matchesQuery.isError &&
    !(matchesQuery.error instanceof ApiError && matchesQuery.error.status === 404)
  ) {
    return (
      <main
        data-testid="game-error"
        className="min-h-screen bg-canvas text-destructive flex flex-col items-center justify-center gap-3 p-6"
      >
        <p className="text-sm font-mono">
          Couldn&rsquo;t load match. Please refresh in a moment.
        </p>
        <BackToMatchesLink
          fromTeamSlug={fromTeamSlug}
          fromSeason={fromSeason}
          className="text-xs text-accent hover:underline font-display uppercase tracking-wider"
        />
      </main>
    );
  }
  if (!match) {
    return (
      <main
        data-testid="game-not-found"
        className="min-h-screen bg-canvas text-ink-muted flex flex-col items-center justify-center gap-3 p-6"
      >
        <h1 className="text-2xl font-display">Match not found</h1>
        <p className="text-sm font-mono">
          The match URL &ldquo;{slug}&rdquo; doesn&rsquo;t resolve.
        </p>
        <BackToMatchesLink
          fromTeamSlug={fromTeamSlug}
          fromSeason={fromSeason}
          className="text-xs text-accent hover:underline font-display uppercase tracking-wider"
        />
      </main>
    );
  }

  return (
    <GameBody
      match={match}
      activeSlot={activeSlot}
      fromTeamSlug={fromTeamSlug}
      fromSeason={fromSeason}
      sortedMatches={sortedMatches}
      settings={settingsQuery.data?.settings}
      clubLogoUrl={settingsQuery.data?.assets?.club_logo_url ?? null}
      clubIconUrl={settingsQuery.data?.assets?.favicon_url ?? null}
      sourceTeamName={sourceTeamQuery.data?.name}
      requestedSeek={recordingSeek((search as {seek?:unknown}).seek)}
      onSelectSlot={(nextSlot) => {
        void navigate({
          to: "/match/$slug/$slot",
          // Emit the hyphenated URL form (matches the legacy
          // public match URLs); `urlSlotToKey` reverses on read.
          params: { slug: match.slug, slot: slotKeyToUrl(nextSlot) },
          // Keep the `?from=` breadcrumb so switching halves doesn't
          // strip the team-page back target.
          search: fromTeamSlug
            ? { from: fromTeamSlug, season: fromSeason }
            : {},
          replace: true,
        });
      }}
    />
  );
}

function GameBody({
  match,
  activeSlot,
  fromTeamSlug,
  fromSeason,
  sortedMatches,
  settings,
  clubLogoUrl,
  clubIconUrl,
  sourceTeamName,
  onSelectSlot,
  requestedSeek,
}: {
  match: Match;
  activeSlot: string | null;
  fromTeamSlug: string | undefined;
  fromSeason: string | undefined;
  sortedMatches: Match[];
  settings: AppSettings | undefined;
  clubLogoUrl: string | null;
  clubIconUrl: string | null;
  sourceTeamName: string | undefined;
  onSelectSlot: (slot: string) => void;
  requestedSeek?: number;
}) {
  const authMe = useAuthStore((s) => s.me);
  // Legacy public game-view header gates (PR #21 P2 fix):
  //   - Edit Match link (→ /admin/matches) → whoever can actually reach the
  //     admin console: `hasAdminAccess` (global admin / club_admin / team_admin).
  //     The legacy `uploader` token that used to gate this was retired in
  //     deprecation PR 3; its writers are now team_admin (via migration 0045).
  //   - Regen Thumb → admin-only; calls the existing admin endpoint
  //     from the public game view for operators watching the match
  //   - "Coach this match →" → coach / assistant_coach / team_admin
  //     membership OR global admin
  const roles = authMe?.user?.roles ?? [];
  const isAdmin = roles.includes("admin");
  const canEdit = hasAdminAccess(authMe);
  const isCoachOrAdmin =
    isAdmin ||
    !!authMe?.memberships?.some((m) =>
      ["coach", "assistant_coach", "team_admin"].includes(m.role),
    );

  const reveal = useScoreRevealStore((s) => s.reveal);
  const revealed = useScoreRevealed(match.id);
  const scoreHidden = hasRecordedScore(match) && !revealed;

  // Prev / next in the date-sorted list (anchor on this match).
  const myIndex = sortedMatches.findIndex((m) => m.id === match.id);
  const prevMatch = myIndex > 0 ? sortedMatches[myIndex - 1] : null;
  const nextMatch =
    myIndex >= 0 && myIndex < sortedMatches.length - 1
      ? sortedMatches[myIndex + 1]
      : null;

  // Document meta — title + OG tags for share-card previews.
  const appName = settings?.app_name || "tktic";
  const title = `${match.home_team} vs ${match.away_team} · ${appName}`;
  const description = `${match.home_team} vs ${match.away_team}${
    match.date ? ` · ${match.date}` : ""
  }${match.location ? ` · ${match.location}` : ""}`;
  const ogImage = match.has_thumbnail
    ? (typeof window !== "undefined" ? window.location.origin : "") +
      `/api/matches/${encodeURIComponent(match.id)}/thumbnail`
    : undefined;
  useDocumentMeta({
    title,
    description,
    ogImage,
    ogType: "video.other",
  });

  return (
    <main
      data-testid="game-shell"
      data-match-id={match.id}
      data-active-slot={activeSlot ?? ""}
      className="min-h-screen bg-canvas text-ink"
    >
      <GameHeader
        match={match}
        prevMatch={prevMatch}
        nextMatch={nextMatch}
        fromTeamSlug={fromTeamSlug}
        fromSeason={fromSeason}
        settings={settings}
        sourceTeamName={sourceTeamName}
      />

      <div className="game-context">
        <p className="eyebrow">
          {[
            longMatchDate(match.date),
            `${match.season_id?.match(/\d{4}/)?.[0] ?? match.date.match(/^\d{4}/)?.[0] ?? "Current"} season`,
            match.time,
            match.location,
            match.tournament_name,
            match.stage,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
        {isCoachOrAdmin &&
        activeSlot &&
        match.video_status?.[activeSlot] === "ready" ? (
          <a
            href={`/coach/review?match_id=${encodeURIComponent(match.id)}&slot=${encodeURIComponent(activeSlot)}`}
            data-testid="game-coach-link"
            className="game-coach-link"
          >
            <PencilLine aria-hidden />
            Open in Coach Review
          </a>
        ) : null}
        <h1 className="sr-only">
          {match.home_team} versus {match.away_team}
        </h1>
      </div>
      <div className="game-scoreboard-wrap">
        <MatchupCard
          match={match}
          clubLogoUrl={clubLogoUrl}
          clubIconUrl={clubIconUrl}
          sourceTeamName={sourceTeamName}
          scoreHidden={scoreHidden}
          onRevealScore={() => reveal(match.id)}
        />
      </div>
      <section className="game-content px-4 py-5 max-w-7xl mx-auto grid grid-cols-1 gap-5 sm:px-6 lg:gap-6">
        <div className="flex flex-col gap-3">
          {match.format === "two_halves" || match.format === "separate" ? (
            <SegmentButtons
              match={match}
              activeSlot={activeSlot}
              onSelect={onSelectSlot}
            />
          ) : null}
          <p className="text-sm text-ink-muted">
            {activeSlot
              ? (SLOT_LABELS[activeSlot as MatchSlot] ?? activeSlot)
              : "Match recording"}{" "}
            · {match.format === "full" ? "Full match" : "Separate recordings"}
          </p>
          {activeSlot && match.video_status?.[activeSlot] === "ready" ? (
            <GameVideo
              match={match}
              slot={activeSlot}
              clubLogoUrl={clubLogoUrl}
              fromTeamSlug={fromTeamSlug}
              fromSeason={fromSeason}
              requestedSeek={requestedSeek}
            />
          ) : (
            <NoVideoPlaceholder
              match={match}
              slot={activeSlot}
              fromTeamSlug={fromTeamSlug}
              fromSeason={fromSeason}
            />
          )}
        </div>

        <aside data-testid="game-sidebar" className="flex flex-col gap-3">
          <SlotStatusList match={match} settings={settings} />
          {settings && coerceBool(settings.downloads_enabled) ? (
            <DownloadList match={match} settings={settings} />
          ) : null}
          {canEdit || isAdmin ? (
            <MatchManagement
              match={match}
              canEdit={canEdit}
              canRegenerateThumbnail={isAdmin}
            />
          ) : null}
        </aside>
      </section>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Header — nav + signed-in extras
// ---------------------------------------------------------------------------

function GameHeader({
  match,
  prevMatch,
  nextMatch,
  fromTeamSlug,
  fromSeason,
  settings,
  sourceTeamName,
}: {
  match: Match;
  prevMatch: Match | null;
  nextMatch: Match | null;
  fromTeamSlug: string | undefined;
  fromSeason: string | undefined;
  settings: AppSettings | undefined;
  sourceTeamName: string | undefined;
}) {
  const configuredBackLabel = settings?.game_back_label?.trim();
  const backLabel =
    configuredBackLabel && configuredBackLabel !== "Back to Matches"
      ? configuredBackLabel
      : `${tenantTeamName(match, sourceTeamName)} matches`;

  return (
    <header
      data-testid="game-header"
      className="px-4 py-3 border-b border-rule flex flex-col items-stretch justify-between gap-3 max-w-7xl mx-auto sm:px-6 md:flex-row md:items-center"
    >
      <div className="game-header-back">
        <BackToMatchesLink
          fromTeamSlug={fromTeamSlug}
          fromSeason={fromSeason}
          label={
            <>
              <ArrowLeft aria-hidden />
              {backLabel}
            </>
          }
          className="text-xs font-display uppercase tracking-wider text-ink-muted hover:text-ink"
        />
      </div>
      <nav className="game-match-navigation" aria-label="Adjacent matches">
        {prevMatch ? (
          <Link
            to="/match/$slug"
            params={{ slug: prevMatch.slug }}
            // Preserve the `?from=` breadcrumb across prev/next so the
            // back link still points at the originating team page.
            search={
              fromTeamSlug
                ? { from: fromTeamSlug, season: fromSeason }
                : undefined
            }
            data-testid="game-prev-link"
            className="game-match-navigation-link"
          >
            <ChevronLeft aria-hidden /> Previous match
          </Link>
        ) : null}
        {nextMatch ? (
          <Link
            to="/match/$slug"
            params={{ slug: nextMatch.slug }}
            search={
              fromTeamSlug
                ? { from: fromTeamSlug, season: fromSeason }
                : undefined
            }
            data-testid="game-next-link"
            className="game-match-navigation-link"
          >
            Next match <ChevronRight aria-hidden />
          </Link>
        ) : null}
      </nav>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Video — HLS playback + heartbeat + keyboard + position memory
// ---------------------------------------------------------------------------

function GameVideo({
  match,
  slot,
  clubLogoUrl,
  fromTeamSlug,
  fromSeason,
  requestedSeek,
}: {
  requestedSeek?: number;
  match: Match;
  slot: string;
  clubLogoUrl: string | null;
  fromTeamSlug: string | undefined;
  fromSeason: string | undefined;
}) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const appliedSeek = useRef<{identity:string;applied:boolean}>({identity:'',applied:false});
  const [killed, setKilled] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  // Key on `match.id`, NOT the `match` object. The stream URLs derive
  // only from the id and the slot, but the match query has a 60s
  // staleTime and refetchOnWindowFocus — so a background refetch hands
  // back a fresh `match` object with identical fields. Keying on the
  // object recomputed these URL strings, which re-ran `<HlsVideo>`'s
  // loading effect (`el.src = ...; el.load()`) and reset `currentTime`
  // to 0. That was the "playback restarts from the beginning after
  // ~a minute" bug — worst on Safari (iOS and macOS), which takes the
  // native-HLS `video.src` path.
  const { hlsUrl, mp4Url } = useMemo(
    () => getPublicStreamUrls({ id: match.id }, slot),
    [match.id, slot],
  );
  const [airPlayHlsState, setAirPlayHlsState] = useState<{
    hlsUrl: string;
    url: string | null;
    pending: boolean;
  }>(() => ({ hlsUrl, url: null, pending: true }));
  const airPlayHlsUrl =
    airPlayHlsState.hlsUrl === hlsUrl ? airPlayHlsState.url : null;
  const airPlayHlsPending =
    airPlayHlsState.hlsUrl !== hlsUrl || airPlayHlsState.pending;

  useEffect(() => {
    const controller = new AbortController();
    setAirPlayHlsState({ hlsUrl, url: null, pending: true });
    resolvePublicAirPlayHlsUrl(hlsUrl, controller.signal)
      .then((url) => {
        if (!controller.signal.aborted) {
          setAirPlayHlsState({ hlsUrl, url, pending: false });
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setAirPlayHlsState({ hlsUrl, url: null, pending: false });
        }
      });
    return () => {
      controller.abort();
    };
  }, [hlsUrl]);

  // `handleUnavailable` MUST be stable: an inline arrow re-ran
  // `<HlsVideo>`'s source-loading effect on every re-render, which
  // reassigns `el.src` and resets `currentTime` to 0.
  //
  // `handleKilled` is memoized only for consistency — `useVodHeartbeat`
  // already holds `onKilled` in a ref and keeps it out of its interval
  // effect's deps, so an unstable identity was never a problem there.
  const handleUnavailable = useCallback(() => setUnavailable(true), []);
  const handleKilled = useCallback(() => {
    setKilled(true);
    videoRef.current?.pause();
  }, []);

  // Heartbeat — handles the 403 kill propagation by pausing the
  // video + flipping `killed` so we can surface a toast.
  useVodHeartbeat({
    videoRef,
    matchId: match.id,
    slot,
    enabled: !unavailable,
    onKilled: handleKilled,
  });

  // Apply the persisted playback speed on mount.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const speed = getPlaybackSpeed();
    // load() resets playbackRate to defaultPlaybackRate while the HLS/MP4
    // source is resolving, so set both before and after metadata arrives.
    video.defaultPlaybackRate = speed;
    video.playbackRate = speed;
  }, [match.id, slot, airPlayHlsPending]);

  // Resume on loadedmetadata; save every 3 s during playback.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let saveInterval: ReturnType<typeof setInterval> | null = null;
    const onLoaded = () => {
      video.playbackRate = getPlaybackSpeed();
      if (requestedSeek !== undefined) return;
      const resume = getResumePosition(match.id, slot, video.duration);
      if (resume != null) {
        try {
          video.currentTime = resume;
        } catch {
          /* seek can throw if metadata not ready */
        }
      }
    };
    const onPlay = () => {
      if (saveInterval) clearInterval(saveInterval);
      saveInterval = setInterval(() => {
        if (!video.paused) {
          savePlaybackPosition(
            match.id,
            slot,
            video.currentTime,
            video.duration,
          );
        }
      }, 3000);
    };
    const onPause = () => {
      if (saveInterval) {
        clearInterval(saveInterval);
        saveInterval = null;
      }
      // One final save on pause so a viewer who pauses mid-match
      // and closes the tab still gets a resume point.
      savePlaybackPosition(match.id, slot, video.currentTime, video.duration);
    };
    video.addEventListener("loadedmetadata", onLoaded);
    const identity=JSON.stringify([match.id,slot,requestedSeek]);
    if (appliedSeek.current.identity!==identity) appliedSeek.current={identity,applied:false};
    const stopExplicitSeek=requestedSeek===undefined?()=>{}:observeRecordingSeek(video,requestedSeek,appliedSeek.current);
    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    return () => {
      video.removeEventListener("loadedmetadata", onLoaded);
      stopExplicitSeek();
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
      if (saveInterval) clearInterval(saveInterval);
    };
  }, [match.id, slot, airPlayHlsPending, requestedSeek]);

  usePublicVideoShortcuts({ videoRef, containerRef: wrapperRef });
  const chromecast = useChromecast(videoRef, () => ({
    url: mp4Url,
    contentType: "video/mp4",
    streamType: "BUFFERED",
    title: `${match.home_team} vs ${match.away_team}`,
    subtitle: SLOT_LABELS[slot as MatchSlot] ?? slot,
    imageUrl:
      clubLogoUrl ||
      (match.opponent_logo ? `/api/matches/${match.id}/opponent-logo` : null),
    currentTime: videoRef.current?.currentTime || 0,
  }));

  if (unavailable) {
    return (
      <div
        ref={wrapperRef}
        data-testid="game-video-unavailable"
        className="scorebook-data-state"
      >
        <span className="scorebook-state-icon" aria-hidden>
          <Inbox />
        </span>
        <h2>This recording isn’t available right now.</h2>
        <p>Try again later, or return to the team’s match library.</p>
        {fromTeamSlug ? (
          <Link
            to="/teams/$teamSlug"
            params={{ teamSlug: fromTeamSlug }}
            search={{ season: fromSeason, view: "matches" }}
            className="td-empty-secondary"
          >
            Back to team
          </Link>
        ) : null}
      </div>
    );
  }

  return (
    <div
      ref={wrapperRef}
      data-testid="game-video-wrapper"
      className="relative flex flex-col gap-2"
    >
      {airPlayHlsPending ? (
        <div
          data-testid="game-video-loading"
          className="flex aspect-video items-center justify-center rounded bg-black text-xs font-mono text-ink-muted"
        >
          Loading video...
        </div>
      ) : (
        <HlsVideo
          ref={videoRef}
          hlsUrl={hlsUrl}
          nativeHlsUrl={airPlayHlsUrl ?? undefined}
          mp4Url={mp4Url}
          controls={false}
          className="w-full aspect-video rounded bg-black"
          onUnavailable={handleUnavailable}
          data-testid="game-video"
        />
      )}
      {killed ? (
        <div
          data-testid="game-video-killed"
          role="alert"
          className="absolute inset-x-0 bottom-12 bg-destructive p-2 text-center font-mono text-xs text-paper"
        >
          This stream was disconnected by an administrator.
        </div>
      ) : null}
      {!airPlayHlsPending ? (
        <PlayerTransport
          videoRef={videoRef}
          frameRef={wrapperRef}
          disabled={killed}
          casting={
            chromecast.supportedBrowser ? (
              <button
                type="button"
                onClick={chromecast.toggle}
                disabled={!chromecast.sdkReady}
                title={chromecast.unavailableReason || undefined}
                data-testid="game-chromecast-btn"
                data-casting={chromecast.casting ? "true" : undefined}
                aria-label={
                  chromecast.casting
                    ? `Stop casting to ${chromecast.deviceName || "screen"}`
                    : "Cast to a screen"
                }
              >
                <Cast aria-hidden />
              </button>
            ) : null
          }
        />
      ) : null}
    </div>
  );
}

function NoVideoPlaceholder({
  match,
  slot,
  fromTeamSlug,
  fromSeason,
}: {
  match: Match;
  slot: string | null;
  fromTeamSlug: string | undefined;
  fromSeason: string | undefined;
}) {
  const selectedStatus = slot ? match.video_status?.[slot] : undefined;
  const anyProcessing = selectedStatus === "transcoding";
  const failed = selectedStatus === "error";
  const optional = slot === "extra_time" || slot === "penalty_shootout";
  return (
    <div data-testid="game-video-placeholder" className="scorebook-data-state">
      <span className="scorebook-state-icon" aria-hidden>
        <Inbox />
      </span>
      {anyProcessing ? (
        <>
          <h2>Recording is being processed.</h2>
          <p>
            Refresh in a few minutes — replay will appear once transcoding
            finishes.
          </p>
        </>
      ) : failed ? (
        <>
          <h2>This recording isn’t available right now.</h2>
          <p>Try again later, or return to the team’s match library.</p>
        </>
      ) : (
        <>
          <h2>No recording for this segment yet.</h2>
          <p>
            {optional
              ? "This optional recording may not be needed. It will appear here if your team adds it."
              : "Your team hasn’t added this part of the match yet."}
          </p>
        </>
      )}
      {fromTeamSlug ? (
        <Link
          to="/teams/$teamSlug"
          params={{ teamSlug: fromTeamSlug }}
          search={{ season: fromSeason, view: "matches" }}
          className="td-empty-secondary"
        >
          Back to team
        </Link>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Segment buttons (two_halves)
// ---------------------------------------------------------------------------

function SegmentButtons({
  match,
  activeSlot,
  onSelect,
}: {
  match: Match;
  activeSlot: string | null;
  onSelect: (slot: string) => void;
}) {
  const readySlots = slotsForMatch(match).filter(
    (slot) => match.video_status?.[slot] === "ready",
  );
  if (readySlots.length === 0) return null;

  return (
    <nav
      data-testid="game-segments"
      role="group"
      aria-label="Match recordings"
      className="flex flex-wrap gap-2"
    >
      {readySlots.map((slot) => {
        const active = activeSlot === slot;
        const label =
          slot === "extra_time" ? "Extra time (overtime)" : SLOT_LABELS[slot];
        return (
          <button
            key={slot}
            type="button"
            onClick={() => onSelect(slot)}
            data-testid={`game-segment-${slot}`}
            data-active={active ? "true" : undefined}
            aria-pressed={active}
            className="rounded border border-rule px-3 py-1.5 text-xs font-display uppercase tracking-wider text-ink-muted transition-colors hover:bg-field hover:text-ink data-[active=true]:border-accent data-[active=true]:bg-accent data-[active=true]:text-accent-ink disabled:cursor-not-allowed disabled:opacity-50"
          >
            {label}
          </button>
        );
      })}
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Sidebar — matchup, slot status, downloads
// ---------------------------------------------------------------------------

function MatchupCard({
  match,
  clubLogoUrl,
  clubIconUrl,
  sourceTeamName,
  scoreHidden,
  onRevealScore,
}: {
  match: Match;
  clubLogoUrl: string | null;
  clubIconUrl: string | null;
  sourceTeamName: string | undefined;
  scoreHidden: boolean;
  onRevealScore: () => void;
}) {
  const { homeUrl, awayUrl } = getSideLogos(match, clubIconUrl || clubLogoUrl);
  const normalizedTeamName = sourceTeamName?.trim().toLocaleLowerCase();
  const homeIsClub = Boolean(
    (match.team_id && match.home_team_id === match.team_id) ||
    (normalizedTeamName &&
      match.home_team.trim().toLocaleLowerCase() === normalizedTeamName),
  );
  const awayIsClub = Boolean(
    (match.team_id && match.away_team_id === match.team_id) ||
    (normalizedTeamName &&
      match.away_team.trim().toLocaleLowerCase() === normalizedTeamName),
  );
  return (
    <article
      data-testid="game-matchup-card"
      className="rounded border border-rule bg-surface p-4 flex flex-col gap-2"
    >
      <SideRow
        side="home"
        name={match.home_team}
        logoUrl={homeUrl}
        fallbackLogoUrl={homeIsClub ? clubLogoUrl : null}
        isClubSide={homeIsClub}
      />
      <div className="game-score-center">
        {hasRecordedScore(match) ? (
          scoreHidden ? (
            <button
              type="button"
              onClick={onRevealScore}
              data-testid="game-matchup-reveal"
              className="self-start rounded border border-rule bg-canvas px-2 py-1 text-[0.6rem] font-display uppercase tracking-wider text-ink-muted hover:bg-field hover:text-ink"
            >
              Reveal score
            </button>
          ) : (
            <p
              data-testid="game-matchup-score"
              className="self-start text-lg font-mono text-ink"
            >
              {match.score_home} – {match.score_away}
              {shootoutLabel(match) ? (
                <span className="ml-1.5 text-sm text-ink-muted">
                  {shootoutLabel(match)}
                </span>
              ) : null}
            </p>
          )
        ) : null}
        <p className="game-result-status">
          {(match.match_status ?? "scheduled").replace(/^./, (c) =>
            c.toUpperCase(),
          )}
        </p>
      </div>
      <SideRow
        side="away"
        name={match.away_team}
        logoUrl={awayUrl}
        fallbackLogoUrl={awayIsClub ? clubLogoUrl : null}
        isClubSide={awayIsClub}
      />
    </article>
  );
}

function SideRow({
  side,
  name,
  logoUrl,
  fallbackLogoUrl,
  isClubSide,
}: {
  side: "home" | "away";
  name: string;
  logoUrl: string | null;
  fallbackLogoUrl: string | null;
  isClubSide: boolean;
}) {
  const initials = name
    .split(/\s+/)
    .map((s) => s.charAt(0).toUpperCase())
    .slice(0, 2)
    .join("");
  return (
    <div
      className={`game-side game-side-${side} flex items-center gap-2 min-w-0`}
    >
      {isClubSide ? (
        <ClubMark
          name={name}
          imageUrl={logoUrl}
          fallbackImageUrl={fallbackLogoUrl}
          className="game-club-mark"
        />
      ) : logoUrl ? (
        <img
          src={logoUrl}
          alt=""
          className="game-uploaded-team-logo h-9 w-9 flex-shrink-0 object-contain"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />
      ) : (
        <div
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded border border-rule bg-canvas text-xs font-display text-ink-muted"
          aria-hidden="true"
        >
          {initials || "?"}
        </div>
      )}
      <div className="flex flex-col min-w-0">
        <span className="game-side-name text-sm font-display text-ink truncate">
          {name}
        </span>
      </div>
    </div>
  );
}

function SlotStatusList({
  match,
  settings,
}: {
  match: Match;
  settings: AppSettings | undefined;
}) {
  const heading = settings?.game_video_status_label || "Video Status";
  const slots = slotsForMatch(match).map(
    (slot) => [slot, match.video_status?.[slot] ?? "none"] as const,
  );
  if (slots.length === 0) return null;
  return (
    <section
      data-testid="game-status-list"
      className="rounded border border-rule bg-surface p-3 flex flex-col gap-1"
    >
      <h2 className="text-[0.55rem] font-display uppercase tracking-wider text-ink-muted">
        {heading}
      </h2>
      <ul className="flex flex-col gap-0.5">
        {slots.map(([slot, status]) => (
          <li
            key={slot}
            className="flex items-center justify-between text-xs font-mono"
          >
            <span className="text-ink-muted">
              {SLOT_LABELS[slot as MatchSlot] ?? slot.replace(/_/g, " ")}
            </span>
            <span
              data-status={status}
              className="text-ink data-[status=transcoding]:text-accent data-[status=error]:text-destructive"
            >
              {status === "none"
                ? "Not uploaded"
                : status.replace(/^./, (character) => character.toUpperCase())}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function MatchManagement({
  match,
  canEdit,
  canRegenerateThumbnail,
}: {
  match: Match;
  canEdit: boolean;
  canRegenerateThumbnail: boolean;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [thumbnailStatus, setThumbnailStatus] = useState<
    "success" | "error" | null
  >(null);
  const [thumbnailMessage, setThumbnailMessage] = useState<string | null>(null);
  const thumbnailMutation = useMutation({
    mutationFn: () => regenerateAdminThumbnail(match.id),
    onMutate: () => {
      setThumbnailStatus(null);
      setThumbnailMessage(null);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: unscopedKey("public-matches"),
        }),
        queryClient.invalidateQueries({
          queryKey: unscopedKey("admin-matches"),
        }),
      ]);
      setThumbnailStatus("success");
      setThumbnailMessage("Thumbnail regeneration started.");
      toast.success("Thumbnail regeneration started");
    },
    onError: (err) => {
      setThumbnailStatus("error");
      setThumbnailMessage(
        err instanceof ApiError && typeof err.detail === "string"
          ? err.detail
          : "Failed to regenerate thumbnail.",
      );
      toast.error("Couldn't regenerate the thumbnail", {
        description: errorMessage(err),
      });
    },
  });

  return (
    <section data-testid="game-management" className="game-management-card">
      <h2>Match management</h2>
      <div className="game-management-actions">
        {canRegenerateThumbnail ? (
          <button
            type="button"
            onClick={() => thumbnailMutation.mutate()}
            disabled={thumbnailMutation.isPending}
            data-testid="game-regenerate-thumbnail"
          >
            <RefreshCw aria-hidden />
            {thumbnailMutation.isPending
              ? "Regenerating…"
              : "Regenerate thumbnail"}
          </button>
        ) : null}
        {canEdit ? (
          <Link to="/admin/matches" data-testid="game-admin-edit-link">
            <PencilLine aria-hidden /> Edit match
          </Link>
        ) : null}
      </div>
      <p>Visible only to authorized administrators.</p>
      {thumbnailMessage ? (
        <span
          role={thumbnailStatus === "error" ? "alert" : "status"}
          data-testid="game-regenerate-thumbnail-status"
          data-status={thumbnailStatus ?? undefined}
          className="data-[status=success]:text-good data-[status=error]:text-destructive"
        >
          {thumbnailMessage}
        </span>
      ) : null}
    </section>
  );
}

function DownloadList({
  match,
  settings,
}: {
  match: Match;
  settings: AppSettings | undefined;
}) {
  const label = settings?.download_label || "Download";
  const readySlots = Object.entries(match.video_status || {}).filter(
    ([, status]) => status === "ready",
  );
  if (readySlots.length === 0) return null;
  return (
    <section
      data-testid="game-downloads"
      className="rounded border border-rule bg-surface p-3 flex flex-col gap-2"
    >
      <h2 className="text-[0.55rem] font-display uppercase tracking-wider text-ink-muted">
        {label}
      </h2>
      <ul className="flex flex-col gap-1">
        {readySlots.map(([slot]) => (
          <li key={slot}>
            <a
              href={`/api/matches/${encodeURIComponent(match.id)}/download/${encodeURIComponent(slot)}`}
              data-testid={`game-download-${slot}`}
              className="text-xs text-accent hover:underline font-mono"
            >
              ↓ {SLOT_LABELS[slot as MatchSlot] ?? slot.replace(/_/g, " ")}
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

// Side-effect-free helper imports to satisfy `import { ApiError }` for
// possible future error introspection. Keeping the import bound so
// a follow-up PR (M11b live + analytics) can branch on `err instanceof ApiError`.
export const _gameShellModuleSentinel: typeof ApiError | undefined = ApiError;
